/**
 * Multi-Select Cell Editor - For tags, types, and other multi-value fields
 */

import React, { useState, useRef, useEffect, useCallback, useMemo } from "react";
import { Check, Search, Loader2, X } from "lucide-react";
import CellEditorWrapper from "./CellEditorWrapper";
import { MultiSelectCellEditorProps, CellOption } from "./types";

const CellEditorMultiSelect: React.FC<MultiSelectCellEditorProps> = ({
  value = [],
  onChange,
  onSave,
  onCancel,
  isVisible,
  cellRect,
  cellWidth,
  options,
  isLoading = false,
  searchable = true,
  placeholder = "Select options...",
}) => {
  const [searchQuery, setSearchQuery] = useState("");
  const [highlightedIndex, setHighlightedIndex] = useState(0);
  const searchInputRef = useRef<HTMLInputElement>(null);
  const listRef = useRef<HTMLDivElement>(null);

  // Ensure value is always an array
  const selectedValues = useMemo(() => {
    if (Array.isArray(value)) return value;
    if (typeof value === "string" && value) return [value];
    return [];
  }, [value]);

  // Filter options based on search
  const filteredOptions = useMemo(() => {
    if (!searchQuery.trim()) return options;
    const query = searchQuery.toLowerCase();
    return options.filter(
      (option) =>
        option.label.toLowerCase().includes(query) ||
        option.value.toLowerCase().includes(query)
    );
  }, [options, searchQuery]);

  // Get selected options for display
  const selectedOptions = useMemo(() => {
    return options.filter((opt) => selectedValues.includes(opt.value));
  }, [options, selectedValues]);

  // Focus search input when visible
  useEffect(() => {
    if (isVisible && searchInputRef.current && searchable) {
      setTimeout(() => {
        searchInputRef.current?.focus();
      }, 50);
    }
  }, [isVisible, searchable]);

  // Reset highlighted index when options change
  useEffect(() => {
    setHighlightedIndex(0);
  }, [filteredOptions]);

  // Scroll highlighted item into view
  useEffect(() => {
    if (listRef.current) {
      const highlightedEl = listRef.current.children[highlightedIndex] as HTMLElement;
      if (highlightedEl) {
        highlightedEl.scrollIntoView({ block: "nearest" });
      }
    }
  }, [highlightedIndex]);

  // Handle option toggle
  const handleToggle = useCallback(
    (option: CellOption) => {
      const isSelected = selectedValues.includes(option.value);
      if (isSelected) {
        onChange(selectedValues.filter((v) => v !== option.value));
      } else {
        onChange([...selectedValues, option.value]);
      }
    },
    [selectedValues, onChange]
  );

  // Handle removing a selected item
  const handleRemove = useCallback(
    (optionValue: string) => {
      onChange(selectedValues.filter((v) => v !== optionValue));
    },
    [selectedValues, onChange]
  );

  // Handle keyboard navigation
  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      switch (e.key) {
        case "ArrowDown":
          e.preventDefault();
          setHighlightedIndex((prev) =>
            Math.min(prev + 1, filteredOptions.length - 1)
          );
          break;
        case "ArrowUp":
          e.preventDefault();
          setHighlightedIndex((prev) => Math.max(prev - 1, 0));
          break;
        case "Enter":
          e.preventDefault();
          if (filteredOptions[highlightedIndex]) {
            handleToggle(filteredOptions[highlightedIndex]);
          }
          break;
        case "Escape":
          e.preventDefault();
          onCancel();
          break;
        case "Backspace":
          if (!searchQuery && selectedValues.length > 0) {
            // Remove last selected item
            onChange(selectedValues.slice(0, -1));
          }
          break;
      }
    },
    [filteredOptions, highlightedIndex, handleToggle, onCancel, searchQuery, selectedValues, onChange]
  );

  return (
    <CellEditorWrapper
      isVisible={isVisible}
      cellRect={cellRect}
      cellWidth={cellWidth}
      onSave={onSave}
      onCancel={onCancel}
      minHeight={44}
      maxHeight={400}
    >
      <div className="flex flex-col h-full" onKeyDown={handleKeyDown}>
        {/* Selected items */}
        {selectedOptions.length > 0 && (
          <div className="flex flex-wrap gap-1 px-2 py-2 border-b border-border bg-muted">
            {selectedOptions.map((option) => (
              <span
                key={option.value}
                className="inline-flex items-center px-2 py-0.5 text-xs font-medium rounded-full bg-primary/10 text-primary"
              >
                {option.color && (
                  <span
                    className="h-2 w-2 rounded-full mr-1"
                    style={{ backgroundColor: option.color }}
                  />
                )}
                {option.label}
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    handleRemove(option.value);
                  }}
                  className="ml-1 hover:bg-primary/20 rounded-full p-0.5"
                >
                  <X className="h-3 w-3" />
                </button>
              </span>
            ))}
          </div>
        )}

        {/* Search input */}
        {searchable && (
          <div className="flex items-center px-2 py-2 border-b border-border">
            <Search className="h-4 w-4 text-muted-foreground mr-2 flex-shrink-0" />
            <input
              ref={searchInputRef}
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder={selectedOptions.length > 0 ? "Add more..." : "Search..."}
              className="flex-1 text-sm border-none outline-none bg-transparent text-foreground"
            />
          </div>
        )}

        {/* Options list */}
        <div ref={listRef} className="flex-1 overflow-y-auto">
          {isLoading ? (
            <div className="flex items-center justify-center py-8 text-muted-foreground">
              <Loader2 className="h-5 w-5 animate-spin mr-2" />
              <span className="text-sm">Loading options...</span>
            </div>
          ) : filteredOptions.length === 0 ? (
            <div className="flex items-center justify-center py-8 text-muted-foreground">
              <span className="text-sm">No options found</span>
            </div>
          ) : (
            filteredOptions.map((option, index) => {
              const isSelected = selectedValues.includes(option.value);
              const isHighlighted = index === highlightedIndex;

              return (
                <div
                  key={option.value}
                  onClick={() => handleToggle(option)}
                  onMouseEnter={() => setHighlightedIndex(index)}
                  className={`
                    flex items-center px-3 py-2 cursor-pointer transition-colors
                    ${isHighlighted ? "bg-primary/10" : ""}
                    hover:bg-primary/10
                  `}
                >
                  {/* Checkbox indicator */}
                  <div
                    className={`
                      h-4 w-4 rounded border mr-2 flex items-center justify-center flex-shrink-0
                      ${isSelected ? "bg-primary border-primary" : "border-border"}
                    `}
                  >
                    {isSelected && <Check className="h-3 w-3 text-primary-foreground" />}
                  </div>

                  {/* Color indicator if provided */}
                  {option.color && (
                    <span
                      className="h-3 w-3 rounded-full mr-2 flex-shrink-0"
                      style={{ backgroundColor: option.color }}
                    />
                  )}

                  {/* Label */}
                  <span className="flex-1 text-sm text-foreground truncate">
                    {option.label}
                  </span>
                </div>
              );
            })
          )}
        </div>

        {/* Selection count */}
        <div className="px-3 py-2 border-t border-border bg-muted">
          <span className="text-xs text-muted-foreground">
            {selectedValues.length} item{selectedValues.length !== 1 ? "s" : ""} selected
          </span>
        </div>
      </div>
    </CellEditorWrapper>
  );
};

export default CellEditorMultiSelect;

